/**
 * 에러 분류 및 처리 유틸리티
 *
 * 인증 에러, 네트워크 에러 등을 분류하고 적절히 처리
 */

import { STORAGE_KEYS, clearAuthData } from './storage';

// 인증 에러 콜백 (authService에서 설정)
let onAuthErrorCallback = null;

/**
 * 인증 에러 발생 시 호출될 콜백 설정
 */
export function setOnAuthError(callback) {
  onAuthErrorCallback = callback;
}

/**
 * 에러 유형 분류
 * @returns {'NETWORK_ERROR' | 'INVALID_TOKEN' | 'TOKEN_EXPIRED' | 'UNKNOWN'}
 */
export function classifyAuthError(error) {
  if (!error) return 'UNKNOWN';

  // 1. 네트워크 에러 판별
  if (
    error.name === 'TypeError' ||
    error.networkError ||
    error.message?.includes('Failed to fetch') ||
    error.message?.includes('NetworkError') ||
    error.message?.includes('Network request failed') ||
    error.message?.includes('timeout') ||
    error.message?.includes('ECONNREFUSED') ||
    error.message?.includes('ETIMEDOUT')
  ) {
    const statusCode = error.statusCode || error.networkError?.statusCode;
    if (statusCode >= 500 && statusCode < 600) {
      return 'NETWORK_ERROR';
    }
    if (!statusCode || error.name === 'TypeError') {
      return 'NETWORK_ERROR';
    }
  }

  // 2. Invalid Token 판별
  const message = (error.message || '').toLowerCase();
  const code = error.extensions?.code || error.code;

  if (
    message === 'invalid token' ||
    message.includes('invalid auth token') ||
    message.includes('jwt malformed') ||
    message.includes('invalid signature') ||
    code === 'INVALID_TOKEN' ||
    code === 'FORBIDDEN'
  ) {
    return 'INVALID_TOKEN';
  }

  // 3. Token Expired 판별
  if (
    message === 'token expired' ||
    message.includes('jwt expired') ||
    message.includes('token not found or expired') ||
    code === 'TOKEN_EXPIRED' ||
    code === 'UNAUTHENTICATED'
  ) {
    return 'TOKEN_EXPIRED';
  }

  return 'UNKNOWN';
}

/**
 * 인증 관련 에러인지 확인
 */
export function isAuthError(errorOrEvent) {
  // 1. WebSocket 종료 코드 체크
  const code = errorOrEvent?.code;
  if (code === 4401 || code === 4403) {
    return true;
  }

  // 2. GraphQL 에러 코드 체크
  const extensions = errorOrEvent?.extensions;
  if (extensions?.code === 'UNAUTHENTICATED' || extensions?.code === 'FORBIDDEN') {
    return true;
  }

  // 3. 정확한 인증 에러 메시지 매칭
  const message = (errorOrEvent?.message || errorOrEvent?.reason || '').toLowerCase();
  const exactAuthErrors = [
    'authentication required',
    'token not found or expired',
    'invalid auth token',
    'missing auth token',
    'jwt expired',
    'jwt malformed',
    'not authenticated',
    'access denied',
    'unauthorized access'
  ];

  if (exactAuthErrors.some(err => message.includes(err))) {
    return true;
  }

  // 4. HTTP 상태 코드 기반 판단
  const statusCode = errorOrEvent?.statusCode || errorOrEvent?.status;
  if (statusCode === 401 || statusCode === 403) {
    return true;
  }

  return false;
}

/**
 * 인증 에러 처리
 * @param {string} source - 에러 발생 위치 (디버깅용)
 * @param {string} errorType - 에러 유형
 */
export async function handleAuthError(source, errorType = 'UNKNOWN') {
  console.log(`[Apollo] 인증 에러 감지 (${source}) - 유형: ${errorType}`);

  // 네트워크 에러: 토큰 유지, 재연결 대기
  if (errorType === 'NETWORK_ERROR') {
    console.log('[Apollo] 네트워크 에러 - 토큰 유지, 재연결 대기');
    window.dispatchEvent(new CustomEvent('network-error', {
      detail: { source, timestamp: Date.now() }
    }));
    return;
  }

  // INVALID_TOKEN, TOKEN_EXPIRED, UNKNOWN: 콜백 실행
  if (onAuthErrorCallback) {
    try {
      await onAuthErrorCallback(errorType);
    } catch (error) {
      console.error('[Apollo] 인증 에러 콜백 실행 실패:', error);
    }
  } else {
    console.warn('[Apollo] 인증 에러 콜백이 설정되지 않음 - 로그인 페이지로 이동');
    clearAuthData();
    window.location.href = '/login';
  }
}
